// Hybrid-only adapter of the approved grid card module; shared live asset stays unchanged.
const normalize = value => String(value || '').trim().toLocaleLowerCase();

const measurementPattern = /\d+(?:\.\d+)?(?:\s*[-–]\s*\d+(?:\.\d+)?)?(?:\s*[x×]\s*\d+(?:\.\d+)?(?:\s*[-–]\s*\d+(?:\.\d+)?)?)*\s*mm/gi;
const beadSizes = value => [...new Set([...String(value || '').matchAll(measurementPattern)].map(match => match[0].replace(/\s*mm/i, '').split(/\s*[x×]\s*/i).map(dimension => dimension.split(/\s*[-–]\s*/).map(Number).join('–')).join(' × ') + ' mm'))];
const romanPattern = /^(?=[MDCLXVI]+$)M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
const romanNumber = value => {
  if (!value || !romanPattern.test(value)) return '';
  const values = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  return String([...value].reduce((sum, letter, i) => sum + (values[letter] < values[value[i + 1]] ? -values[letter] : values[letter]), 0));
};

export function cardLabel(candidate) {
  const title = String(candidate.title || '').trim();
  const category = `${candidate.productType || ''} ${title}`;
  const isSingleBracelet = /\bbracelets?\b/i.test(candidate.productType || title) && !/\b(?:sets?|packs?|bundles?|pairs?)\b/i.test(category);
  if (!isSingleBracelet) return { name: String(candidate.cardName || '').trim() || title, size: '', sizeConflict: false, designRoman: '' };
  const design = title.match(/\bbracelet\s+([IVXLCDM]+)(?=\s|\(|$)/i);
  const designNumber = romanNumber(design?.[1].toUpperCase());
  let name = title.replace(measurementPattern, '').replace(/\(\s*\)/g, '');
  name = name.replace(designNumber ? /\bbracelet\s+[IVXLCDM]+(?=\s|\(|$)/i : /\bbracelet\b/i, '');
  const parts = name.split(/\s+-\s+/).map(part => part.trim()).filter(Boolean);
  name = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' & ' + parts.at(-1) : name;
  name = name.replace(/\s*,\s*,/g, ',').replace(/^[\s,]+|[\s,]+$/g, '').replace(/\s+/g, ' ').toLowerCase();
  name = name.replace(/\b(london|montana|burmese|guatemalan|ethiopian|botswana|picasso|miyuki|african|australian|peruvian|indonesian|brazilian|canadian|tibetan|afghan)\b/g, word => word[0].toUpperCase() + word.slice(1));
  name = name ? name[0].toUpperCase() + name.slice(1) : title;
  const exactSizes = beadSizes(candidate.exactSize), titleSizes = beadSizes(title);
  const sizeConflict = exactSizes.length > 0 && titleSizes.length > 0 && [...exactSizes].sort().join('|') !== [...titleSizes].sort().join('|');
  const sizes = exactSizes.length ? exactSizes : titleSizes;
  return { name: String(candidate.cardName || '').trim() || name, size: sizeConflict ? '' : sizes.join(' / '), sizeConflict, designRoman: designNumber ? design[1].toUpperCase() : '' };
}

export function cardLabels(candidates) {
  const labels = candidates.map(cardLabel);
  return labels.map((label, index) => {
    const peers = labels.map((other, i) => ({ ...other, index: i })).filter(other => normalize(other.name) === normalize(label.name) && normalize(other.size) === normalize(label.size));
    if (peers.length > 1 && (!label.designRoman || peers.filter(peer => peer.designRoman === label.designRoman).length !== 1)) return { ...label, name: candidates[index].title };
    return { ...label, name: label.designRoman ? `${label.name}\u00a0${label.designRoman}` : label.name };
  });
}


export function missingStoneFacets(href, activeValues = []) {
  const url = new URL(href);
  const parameter = 'filter.p.m.custom.stone';
  const selected = [...new Set(url.searchParams.getAll(parameter).flatMap(value => value.split(',')).filter(Boolean))];
  return selected.filter(value => !activeValues.some(active => normalize(active.value) === normalize(value) || normalize(active.label) === normalize(value))).map(value => {
    const removal = new URL(url);
    removal.searchParams.delete(parameter);
    removal.searchParams.delete('page');
    selected.filter(other => other !== value).forEach(other => removal.searchParams.append(parameter, other));
    return { value, label: value.startsWith('gid://') ? '' : value, href: removal.pathname + removal.search + removal.hash };
  });
}

export function catalogueHref(href, sortBy) {
  const url = new URL(href);
  if (!url.searchParams.get('sort_by')) url.searchParams.set('sort_by', sortBy);
  return url.pathname + url.search + url.hash;
}

const syncStoneFacets = () => {
  document.querySelectorAll('[data-abmh-active-facets]').forEach(container => {
    const region = container.parentElement;
    const template = region.querySelector('template[data-abmh-stone-facet]');
    if (!template) return;
    const native = [...container.querySelectorAll('[data-abmh-filter-param="filter.p.m.custom.stone"]')];
    const missing = missingStoneFacets(location.href, native.map(node => ({ value: node.dataset.abmhFilterValue, label: node.dataset.abmhFilterLabel })));
    const existing = [...container.querySelectorAll('[data-abmh-raw-stone]')];
    existing.filter(node => !missing.some(item => item.value === node.dataset.abmhRawStone)).forEach(node => node.remove());
    missing.forEach(item => {
      let node = existing.find(node => node.dataset.abmhRawStone === item.value);
      if (!node) {
        node = template.content.firstElementChild.cloneNode(true);
        node.dataset.abmhRawStone = item.value;
        const label = item.label || template.dataset.stoneLabel;
        node.querySelector('[data-abmh-stone-label]').textContent = label;
        const link = node.querySelector('a');
        link.setAttribute('aria-label', link.getAttribute('aria-label').replace('__STONE__', label));
        container.insertBefore(node, container.querySelector('facet-link:has(.facets-clear-all)'));
      }
      const link = node.querySelector('a');
      const href = catalogueHref(new URL(item.href, location.href).href, template.dataset.sortBy);
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    });
    let fallbackClear = container.querySelector('[data-abmh-fallback-clear]');
    if (!missing.length) fallbackClear?.remove();
    else if (!container.querySelector('.facets-clear-all') && container.querySelectorAll('.removable-facet').length > 1) {
      fallbackClear = region.querySelector('template[data-abmh-clear-facets]').content.firstElementChild.cloneNode(true);
      container.appendChild(fallbackClear);
    }
    if (fallbackClear?.isConnected) {
      const clear = new URL(location.href);
      [...clear.searchParams.keys()].filter(key => key.startsWith('filter.') || key === 'page').forEach(key => clear.searchParams.delete(key));
      const href = catalogueHref(clear.href, template.dataset.sortBy);
      const link = fallbackClear.querySelector('a');
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    }
    const catalogue = container.closest('#abmh-catalog');
    catalogue?.querySelectorAll('form.facets').forEach(form => {
      const inputs = [...form.querySelectorAll('[data-abmh-raw-input]')];
      inputs.filter(input => !missing.some(item => item.value === input.value)).forEach(input => input.remove());
      missing.forEach(item => {
        if (inputs.some(input => input.value === item.value)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'filter.p.m.custom.stone';
        input.value = item.value;
        input.dataset.abmhRawInput = '';
        form.appendChild(input);
      });
    });
    const count = container.querySelectorAll('.removable-facet').length;
    const button = catalogue?.querySelector('button[aria-controls^="abmh-facets-"]');
    if (button) {
      const label = template.dataset.filterLabel + (count ? ' (' + count + ')' : '');
      if (button.textContent.trim() !== label) button.textContent = label;
    }
    container.hidden = !count;
  });
};

const readCards = (root = document) => {
  const metadata = Object.create(null);
  root.querySelectorAll('#abgf-card-data, [data-abgf-card-data]').forEach(node => {
    try { Object.assign(metadata, JSON.parse(node.textContent || '{}')); } catch {}
  });
  return metadata;
};

const cardData = Object.create(null);
const metadataRequests = new Map();
const requestedHandles = new Set();

const loadMetadata = url => {
  url.searchParams.set('section_id', 'ab-gridfirst-cards');
  url.searchParams.delete('sections');
  const previewId = previewThemeId();
  if (previewId) url.searchParams.set('preview_theme_id', previewId);
  const key = url.toString();
  if (!metadataRequests.has(key)) {
    metadataRequests.set(key, (async () => {
      try {
        const response = await fetch(key, { credentials: 'same-origin', signal: AbortSignal.timeout(8000) });
        if (!response.ok) return false;
        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const metadata = readCards(fragment);
        Object.assign(cardData, metadata);
        return Object.keys(metadata).some(handle => handle !== '_');
      } catch {
        // Preserve native names and omit unverified sizes when metadata is unavailable.
        return false;
      }
    })());
  }
  return metadataRequests.get(key);
};

const hydrateMissing = async handles => {
  const missing = handles.filter(handle => handle && !cardData[handle] && !requestedHandles.has(handle));
  if (!missing.length) return;
  missing.forEach(handle => requestedHandles.add(handle));
  const loaded = await loadMetadata(new URL(location.href));
  const root = window.Shopify?.routes?.root || '/';
  if (loaded) await Promise.all(missing.filter(handle => !cardData[handle]).map(handle =>
    loadMetadata(new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin))
  ));
  labelCards();
};

const previewThemeId = () => {
  const requested = new URL(location.href).searchParams.get('preview_theme_id');
  const theme = window.Shopify?.theme;
  return theme?.role === 'unpublished' ? String(theme.id) : requested;
};

const decorate = link => {
  const id = previewThemeId();
  if (!id) return;
  const url = new URL(link.getAttribute('href'), location.origin);
  if (url.origin !== location.origin) return;
  url.searchParams.set('preview_theme_id', id);
  link.setAttribute('href', url.pathname + url.search + url.hash);
};

const keepView = () => {
  const view = new URLSearchParams(location.search).get('view');
  if (!view) return;
  document.querySelectorAll('form.facets, form[action*="/collections/"]').forEach(form => {
    if (form.querySelector('input[name="view"]')) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'view';
    input.value = view;
    form.appendChild(input);
  });
};

let applying = false;

const labelCards = () => {
  syncStoneFacets();
  // Card names and bead sizes are rendered server-side by the theme card (snippets/card-title.liquid,
  // Theme settings > Product card > Title); this only keeps product links on a preview theme.
  applying = true;
  try {
    document.querySelectorAll('[data-abgf-design] .product-list product-card a[href]').forEach(decorate);
  } finally {
    requestAnimationFrame(() => { applying = false; });
  }
};

const observedSections = new WeakSet();

const boot = () => {
  if (!document.querySelector('[data-abgf-design]')) return;
  Object.assign(cardData, readCards());
  keepView();
  labelCards();
  document.querySelectorAll('[data-abgf-design]').forEach(region => {
    const section = region.closest('.shopify-section');
    if (!section || observedSections.has(section)) return;
    observedSections.add(section);
    let timer;
    new MutationObserver(() => {
      if (applying) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        keepView();
        labelCards();
      }, 80);
    }).observe(section, { childList: true, subtree: true });
  });
};

if (typeof document !== 'undefined') {
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-abgf-more-filters]');
    if (!button) return;
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    panel.toggleAttribute('data-abgf-collapsed', !expanded);
  });
  document.addEventListener('facet:update', event => {
    const view = new URLSearchParams(location.search).get('view');
    if (view && event.detail?.url) event.detail.url.searchParams.set('view', view);
  });
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
